import * as React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { Link, To } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import {
    faChevronRight,
    faCircleCheck,
    faMagnifyingGlass,
    faSpinnerThird,
    faTriangleExclamation,
} from '@fortawesome/pro-regular-svg-icons';
import Button from '../../../core/components/Button.tsx';
import Checkbox from '../../../core/components/Checkbox.tsx';
import StringField from '../../../core/components/Fields/StringField.tsx';
import { withIcon } from '../../../core/components/Icon.tsx';
import Modal from '../../../core/components/Modal.tsx';
import { useToaster } from '../../../core/components/Toast.tsx';
import { useApplicationContext } from '../../../core/contexts/ApplicationContext.tsx';
import classnames from '../../../core/utils/classnames.tsx';
import { canConfigureLlmAdvancedSettings } from '../../../users/utils.ts';
import { useGetFieldLabel, useGetInputLabel, useLlmExtractorInputs } from '../../llmExtractorFields.ts';
import { APPLY_LLM_EXTRACTOR_CONFIG, GET_LLM_EXTRACTOR_CONFIG_APPLY_PREVIEW } from '../../queries.ts';
import {
    ApplyOutcome,
    ApplyTarget,
    getAppliedRule,
    getPassages,
    getWordSegments,
    groupByChange,
    Passage,
    Segment,
} from './applyRule.ts';
import { getRuleScopeTitle, Rule, RULE_LEVELS, RuleLevel } from './rules.ts';

const CheckIcon = withIcon(faCircleCheck);
const ChevronIcon = withIcon(faChevronRight);
const SearchIcon = withIcon(faMagnifyingGlass);
const SpinnerIcon = withIcon(faSpinnerThird);
const WarningIcon = withIcon(faTriangleExclamation);

const SEARCH_FROM = 15;

type ApplyResult = 'updated' | 'added' | 'failed';

interface ChannelApplyResult {
    channelId: string;
    success: boolean;
}

interface ChipProps extends React.ComponentPropsWithRef<'span'> {
    change: Segment['type'];
}

const Chip = ({ change, className, ...props }: ChipProps) => (
    <span
        {...props}
        className={classnames(
            'inline-flex items-center rounded border border-solid px-1.5 text-xs',
            change === 'added' && 'border-transparent bg-surface-success-weak text-content-success-strong',
            change === 'removed' && 'border-transparent bg-surface-danger-weak text-content-danger-strong line-through',
            change === 'same' && 'border-line-weak text-content-strong',
            className
        )}
    />
);

const CardRow = ({ className, ...props }: React.ComponentPropsWithRef<'div'>) => (
    <div
        {...props}
        className={classnames(
            'border-0 border-t border-solid border-line-subtle px-4 py-2 first:border-t-0',
            className
        )}
    />
);

interface SettingChange {
    label: string;
    kind: 'list' | 'value';
    before: string[];
    after: string[];
}

const useGetSettingChanges = (level: RuleLevel, inputLabel: (name: string) => string) => {
    const { t } = useTranslation('config');
    const { user } = useApplicationContext();
    const getFieldLabel = useGetFieldLabel();
    const onOff = (isOn?: boolean) => [
        isOn ? t('configPage.fineTuning.apply.on') : t('configPage.fineTuning.apply.off'),
    ];
    const effort = (value?: string | null) =>
        value ? [t(`configPage.fineTuning.settings.reasoningEfforts.${value}`)] : [];
    const fileNames = (rule: Rule) => (rule.contextFiles ?? []).map(({ name }) => name);

    return (current: Rule, applied: Rule, target: ApplyTarget): SettingChange[] => {
        const filesAfter = [
            ...fileNames(current).filter((name) => !target.filesToRemove.includes(name)),
            ...target.filesToAdd,
        ];
        const changes: SettingChange[] = [
            {
                label: t('configPage.fineTuning.settings.name'),
                kind: 'value',
                before: current.name ? [current.name] : [],
                after: applied.name ? [applied.name] : [],
            },
            {
                label: t('configPage.fineTuning.outputFields'),
                kind: 'list',
                before: (current.fields ?? []).map(getFieldLabel),
                after: (applied.fields ?? []).map(getFieldLabel),
            },
            {
                label: t('configPage.fineTuning.apply.inputs'),
                kind: 'list',
                before: (current.documentInput ?? []).map(inputLabel),
                after: (applied.documentInput ?? []).map(inputLabel),
            },
            {
                label: t('configPage.fineTuning.settings.knowledge'),
                kind: 'list',
                before: fileNames(current),
                after: filesAfter,
            },
            {
                label: t('configPage.fineTuning.settings.tools'),
                kind: 'list',
                before: current.tools ?? [],
                after: applied.tools ?? [],
            },
            {
                label: t('configPage.fineTuning.settings.learnFromCorrections'),
                kind: 'value',
                before: onOff(current.useRag),
                after: onOff(applied.useRag),
            },
            {
                label: t('configPage.fineTuning.settings.testDocumentsOnly'),
                kind: 'value',
                before: onOff(current.testingOnly),
                after: onOff(applied.testingOnly),
            },
        ];
        // the backend copies these for Workist users only, the same users who see them
        if (canConfigureLlmAdvancedSettings(user)) {
            changes.push(
                {
                    label: t('configPage.fineTuning.settings.model'),
                    kind: 'value',
                    before: current.model ? [current.model] : [],
                    after: applied.model ? [applied.model] : [],
                },
                {
                    label: t('configPage.fineTuning.settings.reasoningEffort'),
                    kind: 'value',
                    before: effort(current.reasoningEffort),
                    after: effort(applied.reasoningEffort),
                }
            );
            if (level === 'lineItem') {
                changes.push({
                    label: t('configPage.fineTuning.settings.lineItemOperations'),
                    kind: 'value',
                    before: onOff(current.lineItemOperationsMode),
                    after: onOff(applied.lineItemOperationsMode),
                });
            }
        }
        // a list's order isn't part of the rule
        return changes.filter(({ before, after }) => before.toSorted().join('\n') !== after.toSorted().join('\n'));
    };
};

const getChangeSummary = (changes: SettingChange[], passages: Passage[], t: TFunction) => {
    const changedPassages = passages.filter(({ type }) => type !== 'same').length;
    return [
        changedPassages > 0 && t('configPage.fineTuning.apply.instructionsChanged', { count: changedPassages }),
        ...changes.map(({ label }) => label),
    ]
        .filter(Boolean)
        .join(' · ');
};

const getListChange = (item: string, before: string[], after: string[]): Segment['type'] => {
    if (!before.includes(item)) return 'added';
    if (!after.includes(item)) return 'removed';
    return 'same';
};

interface ChangeDetailsProps {
    changes: SettingChange[];
    passages: Passage[];
}

const ChangeDetails = ({ changes, passages }: ChangeDetailsProps) => {
    const { t } = useTranslation('config');
    const unchangedPassages = passages.filter(({ type }) => type === 'same').length;
    return (
        <div className="flex flex-col gap-3">
            {changes.map(({ label, kind, before, after }) => (
                <div key={label} className="flex flex-col gap-1">
                    <span className="text-xs font-medium text-content-medium">{label}</span>
                    <span className="flex flex-wrap gap-1.5">
                        {kind === 'value' ? (
                            <>
                                {before[0] && <Chip change="removed">{before[0]}</Chip>}
                                {after[0] && <Chip change="added">{after[0]}</Chip>}
                            </>
                        ) : (
                            [...new Set([...before, ...after])].toSorted().map((item) => (
                                <Chip key={item} change={getListChange(item, before, after)}>
                                    {item}
                                </Chip>
                            ))
                        )}
                    </span>
                </div>
            ))}
            {unchangedPassages === passages.length ? (
                <span className="text-xs text-content-medium">{t('configPage.fineTuning.apply.instructionsSame')}</span>
            ) : (
                <>
                    {passages
                        .filter(({ type }) => type !== 'same')
                        .map((passage, index) => (
                            <div key={index} className="flex flex-col gap-1">
                                <span className="text-xs font-medium text-content-medium">
                                    {t(`configPage.fineTuning.apply.passage.${passage.type}`)}
                                </span>
                                <div
                                    className={classnames(
                                        'whitespace-pre-wrap rounded-lg border border-solid border-line-weak',
                                        'bg-surface-medium p-3 text-xs text-content-strong'
                                    )}
                                >
                                    {getWordSegments(passage.before, passage.after).map((segment, segmentIndex) => (
                                        <span
                                            key={segmentIndex}
                                            className={classnames(
                                                segment.type === 'removed' &&
                                                    'bg-surface-danger-weak text-content-danger-strong line-through',
                                                segment.type === 'added' &&
                                                    'bg-surface-success-weak text-content-success-strong'
                                            )}
                                        >
                                            {segment.text}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        ))}
                    {unchangedPassages > 0 && (
                        <span className="text-xs text-content-medium">
                            {t('configPage.fineTuning.apply.passagesSame', { count: unchangedPassages })}
                        </span>
                    )}
                </>
            )}
        </div>
    );
};

interface ApplyCardProps {
    checkbox: React.ReactNode;
    title: string;
    detail?: string;
    isMuted?: boolean;
    isOpen: boolean;
    onToggle: () => void;
    children: React.ReactNode;
}

const ApplyCard = ({ checkbox, title, detail, isMuted = false, isOpen, onToggle, children }: ApplyCardProps) => (
    <div className="flex flex-col overflow-hidden rounded-lg border border-solid border-line-weak">
        <div className={classnames('relative flex gap-3 px-4 py-3', detail ? 'items-start' : 'items-center')}>
            <span className={classnames('flex h-5 w-3 flex-none items-center', detail && 'mt-0.5')}>
                <ChevronIcon
                    className={classnames('text-xs text-content-weak transition-transform', isOpen && 'rotate-90')}
                />
            </span>
            {/* above the button's click area, which covers the whole header */}
            <span className={classnames('relative z-10', detail && 'pt-0.5')}>{checkbox}</span>
            <button
                type="button"
                aria-expanded={isOpen}
                className="flex min-w-0 flex-1 cursor-pointer flex-col gap-0.5 text-left after:absolute after:inset-0"
                onClick={onToggle}
            >
                <span
                    className={classnames(
                        'text-sm font-medium',
                        isMuted ? 'text-content-medium' : 'text-content-strong'
                    )}
                >
                    {title}
                </span>
                {detail && <span className="text-xs text-content-medium">{detail}</span>}
            </button>
        </div>
        {isOpen && children}
    </div>
);

interface ResultRowProps {
    channelName: string;
    result: ApplyResult;
}

const ResultRow = ({ channelName, result }: ResultRowProps) => {
    const { t } = useTranslation('config');
    return (
        <CardRow className="flex items-center gap-3">
            <span className="flex-1 truncate text-sm text-content-strong">{channelName}</span>
            <span
                className={classnames(
                    'inline-flex items-center gap-1.5 text-xs',
                    result === 'failed' ? 'text-content-danger-strong' : 'text-content-success-strong'
                )}
            >
                {result === 'failed' ? <WarningIcon /> : <CheckIcon />}
                {t(`configPage.fineTuning.apply.result.${result}`)}
            </span>
        </CardRow>
    );
};

const getNames = (targets: ApplyTarget[], t: TFunction) => {
    const names = targets.map(({ channelName }) => channelName);
    if (names.length <= 3) return names.join(', ');
    return t('configPage.fineTuning.apply.namesAndMore', {
        names: names.slice(0, 3).join(', '),
        count: names.length - 3,
    });
};

interface GroupCheckboxProps {
    targets: ApplyTarget[];
    selectedIds: Set<string>;
    onSelect: (ids: string[], isSelected: boolean) => void;
}

const GroupCheckbox = ({ targets, selectedIds, onSelect }: GroupCheckboxProps) => {
    const ids = targets.map(({ channelId }) => channelId);
    const selectedCount = ids.filter((id) => selectedIds.has(id)).length;
    return (
        <Checkbox
            checked={selectedCount > 0 && selectedCount === ids.length}
            indeterminate={selectedCount > 0 && selectedCount < ids.length}
            onChange={() => onSelect(ids, selectedCount < ids.length)}
        />
    );
};

interface ChannelRowProps {
    target: ApplyTarget;
    rule: Rule;
    isSelectable: boolean;
    isSelected: boolean;
    inputLabel: (name: string) => string;
    getRuleUrl: (channelId: string, ruleId: string) => To;
    onSelect: (isSelected: boolean) => void;
}

const ChannelRow = ({ target, rule, isSelectable, isSelected, inputLabel, getRuleUrl, onSelect }: ChannelRowProps) => {
    const { t } = useTranslation('config');
    const getFieldLabel = useGetFieldLabel();
    const clients = target.config?.partitionIds ?? [];
    const extraClients = clients.filter((client) => !rule.partitionIds?.includes(client));
    const leftOut = [...target.leftOutFields.map(getFieldLabel), ...target.leftOutInputs.map(inputLabel)];
    const note = [
        target.outcome === 'UPDATE' &&
            target.config?.isEnabled === false &&
            t('configPage.fineTuning.apply.staysInactive'),
        target.outcome === 'UPDATE' &&
            !target.isRecommended &&
            t('configPage.fineTuning.apply.alsoRunsFor', {
                count: extraClients.length,
                clients: extraClients.join(', '),
            }),
        target.outcome === 'UPDATE' &&
            target.isRecommended &&
            clients.join() !== (rule.partitionIds ?? []).join() &&
            t('configPage.fineTuning.apply.stillRunsFor', { count: clients.length, clients: clients.join(', ') }),
        target.outcome === 'ADD' && target.config && t('configPage.fineTuning.apply.fillsEmptyDefault'),
        leftOut.length > 0 && t('configPage.fineTuning.apply.leftOut', { names: leftOut.join(', ') }),
    ]
        .filter(Boolean)
        .join(' · ');
    return (
        <CardRow className="flex items-center gap-3 pl-10">
            <label
                className={classnames('group flex min-w-0 flex-1 items-center gap-3', isSelectable && 'cursor-pointer')}
            >
                <span className="flex w-5 justify-center">
                    <Checkbox
                        size="sm"
                        checked={target.outcome === 'SAME' || isSelected}
                        disabled={!isSelectable}
                        onChange={(event) => onSelect(event.target.checked)}
                    />
                </span>
                <span className="flex-1 truncate text-sm text-content-strong">{target.channelName}</span>
            </label>
            {note && <span className="text-right text-xs text-content-medium">{note}</span>}
            {target.outcome === 'BLOCKED' && target.config && (
                <Link
                    to={getRuleUrl(target.channelId, target.config.id)}
                    className="text-xs font-medium text-content-brand-strong hover:underline"
                >
                    {t('configPage.fineTuning.apply.openRule')}
                </Link>
            )}
        </CardRow>
    );
};

interface UpdateCardProps extends Omit<ApplyCardProps, 'title' | 'detail' | 'isMuted'> {
    group: ApplyTarget[];
    rule: Rule;
    getSettingChanges: ReturnType<typeof useGetSettingChanges>;
}

const UpdateCard = ({ group, rule, getSettingChanges, children, ...props }: UpdateCardProps) => {
    const { t } = useTranslation('config');
    const [first] = group;
    if (!first.config) return null;
    const applied = getAppliedRule(rule, first);
    const changes = getSettingChanges(first.config, applied, first);
    const passages = getPassages(first.config.prompt ?? '', applied.prompt ?? '');
    return (
        <ApplyCard
            {...props}
            title={t('configPage.fineTuning.apply.updateTitle', { channels: getNames(group, t) })}
            detail={getChangeSummary(changes, passages, t)}
        >
            <CardRow className="py-3">
                <ChangeDetails changes={changes} passages={passages} />
            </CardRow>
            {children}
        </ApplyCard>
    );
};

const OTHER_TITLE_KEYS = { ADD: 'addTitle', BLOCKED: 'blockedTitle', SAME: 'sameTitle' };

interface OtherCardProps extends Omit<ApplyCardProps, 'title' | 'detail' | 'isMuted'> {
    outcome: 'ADD' | 'BLOCKED' | 'SAME';
    all: ApplyTarget[];
    shown: ApplyTarget[];
}

const OtherCard = ({ outcome, all, shown, isOpen, ...props }: OtherCardProps) => {
    const { t } = useTranslation('config');
    const blockedReason = (target: ApplyTarget) =>
        target.takenClient
            ? t('configPage.fineTuning.apply.clientTaken', { client: target.takenClient })
            : t('configPage.fineTuning.apply.defaultTaken');
    let detail = isOpen ? undefined : getNames(shown, t);
    if (outcome === 'BLOCKED') detail = [...new Set(all.map(blockedReason))].join(' · ');
    return (
        <ApplyCard
            {...props}
            isOpen={isOpen}
            title={t(`configPage.fineTuning.apply.${OTHER_TITLE_KEYS[outcome]}`, { count: all.length })}
            detail={detail}
            isMuted={outcome !== 'ADD'}
        />
    );
};

interface ApplyRuleModalProps {
    level: RuleLevel;
    rule: Rule;
    channelId: string;
    gqlDocumentType: string;
    getRuleUrl: (channelId: string, ruleId: string) => To;
    onClose: () => void;
}

const ApplyRuleModal = ({ level, rule, channelId, gqlDocumentType, getRuleUrl, onClose }: ApplyRuleModalProps) => {
    const { t } = useTranslation('config');
    const { publishToast } = useToaster();
    const getInputLabel = useGetInputLabel();
    // the rule's inputs are this channel's, so their labels come from here
    const inputs = useLlmExtractorInputs(channelId, gqlDocumentType, level);
    const inputLabel = (name: string) => getInputLabel(inputs.find((input) => input.name === name) ?? { name });
    const getSettingChanges = useGetSettingChanges(level, inputLabel);

    const variables = { documentType: gqlDocumentType, channelId, configId: rule.id };
    const preview = useQuery<{ targets: ApplyTarget[] }>(GET_LLM_EXTRACTOR_CONFIG_APPLY_PREVIEW, {
        variables,
        fetchPolicy: 'network-only',
    });
    const [applyRule, { loading: isApplying }] = useMutation(APPLY_LLM_EXTRACTOR_CONFIG);

    const [chosenIds, setChosenIds] = useState<Set<string> | null>(null);
    const [openGroups, setOpenGroups] = useState<string[]>([]);
    const [search, setSearch] = useState('');
    const [results, setResults] = useState<Record<string, ApplyResult> | null>(null);

    const targets = preview.data?.targets ?? [];
    const targetsWith = (outcome: ApplyOutcome) => targets.filter((target) => target.outcome === outcome);
    const updateGroups = groupByChange(targetsWith('UPDATE'));
    const selectable = targets.filter(({ outcome }) => outcome === 'UPDATE' || outcome === 'ADD');
    const selectedIds =
        chosenIds ??
        new Set(
            targetsWith('UPDATE')
                .filter(({ isRecommended }) => isRecommended)
                .map(({ channelId: id }) => id)
        );
    const setSelected = (ids: string[], isSelected: boolean) => {
        const next = new Set(selectedIds);
        ids.forEach((id) => (isSelected ? next.add(id) : next.delete(id)));
        setChosenIds(next);
    };

    const needle = search.trim().toLowerCase();
    const matches = (target: ApplyTarget) => target.channelName.toLowerCase().includes(needle);
    const isOpen = (key: string) => !!needle || openGroups.includes(key);
    const toggle = (key: string) =>
        setOpenGroups((open) => (open.includes(key) ? open.filter((item) => item !== key) : [...open, key]));

    const resultEntries = Object.entries(results ?? {});
    const failedIds = resultEntries.filter(([, result]) => result === 'failed').map(([id]) => id);
    const appliedCount = resultEntries.length - failedIds.length;

    const apply = async (targetChannelIds: string[]) => {
        const resultOf = (id: string): ApplyResult =>
            targets.find((target) => target.channelId === id)?.outcome === 'UPDATE' ? 'updated' : 'added';
        let appliedIds = new Set<string>();
        try {
            const { data } = await applyRule({ variables: { ...variables, targetChannelIds } });
            const channelResults: ChannelApplyResult[] = data?.applyLlmExtractorConfig?.results ?? [];
            appliedIds = new Set(channelResults.filter(({ success }) => success).map(({ channelId: id }) => id));
        } catch (e) {
            console.error(e);
        }
        setResults((current) => ({
            ...current,
            ...Object.fromEntries(targetChannelIds.map((id) => [id, appliedIds.has(id) ? resultOf(id) : 'failed'])),
        }));
    };

    const close = () => {
        if (isApplying) return;
        if (appliedCount) {
            publishToast({
                description: t('configPage.fineTuning.apply.toast', { count: appliedCount }),
                status: 'success',
            });
        }
        onClose();
    };

    const channelRows = (shown: ApplyTarget[]) =>
        shown.map((target) => (
            <ChannelRow
                key={target.channelId}
                target={target}
                rule={rule}
                isSelectable={selectable.includes(target)}
                isSelected={selectedIds.has(target.channelId)}
                inputLabel={inputLabel}
                getRuleUrl={getRuleUrl}
                onSelect={(isSelected) => setSelected([target.channelId], isSelected)}
            />
        ));

    const body = () => {
        if (preview.error) {
            return <p className="text-sm text-content-strong">{t('configPage.fineTuning.apply.loadError')}</p>;
        }
        if (preview.loading) {
            return (
                <span className="inline-flex items-center gap-2 py-6 text-sm text-content-medium">
                    <SpinnerIcon className="animate-spin" />
                    {t('configPage.fineTuning.apply.loading')}
                </span>
            );
        }
        if (results) {
            return (
                <>
                    <span className="text-sm text-content-strong">
                        {failedIds.length
                            ? t('configPage.fineTuning.apply.appliedSome', {
                                  applied: appliedCount,
                                  count: resultEntries.length,
                              })
                            : t('configPage.fineTuning.apply.applied', { count: resultEntries.length })}
                    </span>
                    <div className="flex flex-col overflow-hidden rounded-lg border border-solid border-line-weak">
                        {resultEntries.map(([id, result]) => (
                            <ResultRow
                                key={id}
                                channelName={targets.find((target) => target.channelId === id)?.channelName ?? ''}
                                result={result}
                            />
                        ))}
                    </div>
                </>
            );
        }
        if (!targets.length) {
            return <p className="text-sm text-content-strong">{t('configPage.fineTuning.apply.noChannels')}</p>;
        }
        return (
            <>
                {!selectable.length && (
                    <p className="text-sm text-content-strong">{t('configPage.fineTuning.apply.nothingToApply')}</p>
                )}
                {targets.length >= SEARCH_FROM && selectable.length > 0 && (
                    <StringField
                        prefix={<SearchIcon />}
                        value={search}
                        onValueChange={(value) => setSearch(value ?? '')}
                        placeholder={t('configPage.fineTuning.apply.search')}
                    />
                )}
                {needle && !targets.some(matches) && (
                    <p className="text-sm text-content-medium">
                        {t('configPage.fineTuning.apply.noMatch', { search })}
                    </p>
                )}
                <div className="flex flex-col gap-3">
                    {updateGroups.map((group) => {
                        const shown = group.filter(matches);
                        const { groupKey } = group[0];
                        return (
                            shown.length > 0 && (
                                <UpdateCard
                                    key={groupKey}
                                    group={group}
                                    rule={rule}
                                    getSettingChanges={getSettingChanges}
                                    checkbox={
                                        <GroupCheckbox
                                            targets={shown}
                                            selectedIds={selectedIds}
                                            onSelect={setSelected}
                                        />
                                    }
                                    isOpen={isOpen(groupKey)}
                                    onToggle={() => toggle(groupKey)}
                                >
                                    {channelRows(shown)}
                                </UpdateCard>
                            )
                        );
                    })}
                    {(['ADD', 'BLOCKED', 'SAME'] as const).map((outcome) => {
                        const all = targetsWith(outcome);
                        const shown = all.filter(matches);
                        return (
                            shown.length > 0 && (
                                <OtherCard
                                    key={outcome}
                                    outcome={outcome}
                                    all={all}
                                    shown={shown}
                                    checkbox={
                                        outcome === 'ADD' ? (
                                            <GroupCheckbox
                                                targets={shown}
                                                selectedIds={selectedIds}
                                                onSelect={setSelected}
                                            />
                                        ) : (
                                            <Checkbox checked={outcome === 'SAME'} disabled />
                                        )
                                    }
                                    isOpen={isOpen(outcome)}
                                    onToggle={() => toggle(outcome)}
                                >
                                    {channelRows(shown)}
                                </OtherCard>
                            )
                        );
                    })}
                </div>
            </>
        );
    };

    const buttons = () => {
        if (isApplying) {
            return [
                <Button key="applying" variant="primary" disabled>
                    {t('configPage.fineTuning.apply.applying')}
                </Button>,
            ];
        }
        const closeButton = (label: string) => (
            <Button key="close" variant="primary" onClick={close}>
                {label}
            </Button>
        );
        if (results) {
            return [
                failedIds.length > 0 && (
                    <Button key="retry" onClick={() => apply(failedIds)}>
                        {t('configPage.fineTuning.apply.retry', { count: failedIds.length })}
                    </Button>
                ),
                closeButton(
                    failedIds.length ? t('configPage.fineTuning.apply.close') : t('configPage.fineTuning.apply.done')
                ),
            ];
        }
        const cancelButton = (
            <Button key="cancel" onClick={close}>
                {t('configPage.document.cancel')}
            </Button>
        );
        if (preview.loading) return [cancelButton];
        if (!selectable.length) return [closeButton(t('configPage.fineTuning.apply.close'))];
        return [
            cancelButton,
            <Button key="apply" variant="primary" disabled={!selectedIds.size} onClick={() => apply([...selectedIds])}>
                {t('configPage.fineTuning.apply.apply', { count: selectedIds.size })}
            </Button>,
        ];
    };

    return (
        <Modal
            title={t('configPage.fineTuning.apply.title')}
            buttons={buttons()}
            onClose={close}
            noCloseButton={isApplying}
            preventCloseOnOverlayClick
        >
            <div className="flex w-[40rem] max-w-full flex-col gap-4 pb-2">
                <span className="text-xs text-content-medium">
                    {[rule.name, getRuleScopeTitle(rule, t), t(RULE_LEVELS[level].titleKey)]
                        .filter(Boolean)
                        .join(' · ')}
                </span>
                {body()}
            </div>
        </Modal>
    );
};

export default ApplyRuleModal;
